import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CodeStatus, EmbeddedCodeLike, EntitySubStatus, FinanceSecurityRight, ItemLinkEntity } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { TooltipModule } from 'primeng/tooltip';
import { resolveEffectiveStatus, resolveSorNormalStyle, resolveStatusStyle } from '../../support/item.status.style';
import { itemStatusCellBridgeStyles, itemStatusCellStyleId } from './item.status.cell.renderer.component.theme';

/** `hideBadges` and `isLocked` are the contractor portal's; the office mounts set neither. */
type ItemStatusCellParams = ICellRendererParams & { sorMode?: boolean; hideBadges?: boolean; isLocked?: (row: unknown) => boolean };

/** One finance-link badge: a single letter that is filled in when the link exists. */
interface ItemFinanceBadge {
  label: string;
  tooltip: string;
  active: boolean;
  url?: string;
}

/**
 * Composite status cell for the items board: the status pill plus the finance-link badges
 * (O ordered / B billed / I invoiced) and a supplied-by-subcontractor icon.
 *
 * Ported from the legacy embedded-codes table, which read precomputed statusName/status*Color off
 * each row. Items rows carry the raw CodeStatus, so the pill styling is resolved here instead.
 *
 * The four badge fields are annotated on the item metadata purely so the view projection keeps them —
 * they are never columns of their own.
 *
 * SOR mode (`sorMode` cell param, set only on the SOR board mount) drops the badges and shows the
 * pill alone: on that board the same finance links are counted by the indicators cell, which owns
 * the "Financial Documents" card.
 */
@Component({
  selector: 'item-status-cell-renderer',
  templateUrl: './item.status.cell.renderer.component.html',
  styleUrls: ['./item.status.cell.renderer.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, TooltipModule],
})
export class ItemStatusCellRendererComponent implements ICellRendererAngularComp {

  private readonly rightsService = inject(RightsService);

  private readonly canAccessPurchaseOrder = this.rightsService.hasRights(FinanceSecurityRight.CanAccessPurchaseOrder);
  private readonly canAccessBill = this.rightsService.hasRights(FinanceSecurityRight.CanAccessBills);
  private readonly canAccessInvoice = this.rightsService.hasRights(FinanceSecurityRight.CanAccessInvoices);

  protected readonly $status = signal<CodeStatus | null>(null);
  protected readonly $itemStatus = signal<EntitySubStatus | null>(null);
  protected readonly $suppliedBySubcontractor = signal(false);
  protected readonly $purchaseOrder = signal<ItemLinkEntity | null>(null);
  protected readonly $bill = signal<ItemLinkEntity | null>(null);
  protected readonly $invoice = signal<ItemLinkEntity | null>(null);

  /** SOR board mount only — drops the letter badges, which that board shows as indicator counters. */
  protected readonly $sorMode = signal(false);

  // O / B / I and the truck are the office's finance links, which a subcontractor does not read.
  protected readonly $hideBadges = signal(false);

  protected readonly $locked = signal(false);

  /** Entity-status name when stamped (SOR / pending); otherwise the legacy CodeStatus string. */
  protected readonly $statusLabel = computed(() => this.$itemStatus()?.name ?? this.$status() ?? '');

  protected readonly $statusStyle = computed(() => {

    const style = resolveStatusStyle(this.$status(), this.$itemStatus());
    const isNormal = resolveEffectiveStatus(this.$status(), this.$itemStatus()) === CodeStatus.Normal;

    return this.$sorMode() && isNormal ? resolveSorNormalStyle(this.$itemStatus()) : style;
  });

  protected readonly $badges = computed<ItemFinanceBadge[]>(() => {

    const purchaseOrder = this.$purchaseOrder();
    const bill = this.$bill();
    const invoice = this.$invoice();

    return [
      { label: 'O', tooltip: purchaseOrder?.name ?? 'Not ordered', active: !!purchaseOrder, url: this.canAccessPurchaseOrder && purchaseOrder?._id ? `/finance/purchase-order/${encodeURIComponent(purchaseOrder._id)}` : undefined },
      { label: 'B', tooltip: bill?.name ?? 'Not billed', active: !!bill, url: this.canAccessBill && bill?._id ? `/finance/bills/${encodeURIComponent(bill._id)}` : undefined },
      { label: 'I', tooltip: invoice?.name ?? 'Not invoiced', active: !!invoice, url: this.canAccessInvoice && invoice?._id ? `/finance/invoices/${encodeURIComponent(invoice._id)}` : undefined },
    ];
  });

  public constructor() {

    DesignSystem.Mount(inject(DOCUMENT), itemStatusCellStyleId, itemStatusCellBridgeStyles);
  }

  public agInit(params: ItemStatusCellParams): void {

    this.read(params);
  }

  public refresh(params: ItemStatusCellParams): boolean {

    this.read(params);
    return true;
  }

  private read(params: ItemStatusCellParams): void {

    // JobItem rows stay parent-shaped after unwind (no $replaceRoot): the item lives at the
    // column prefix (e.g. field `workOrders.codes.status` → item at `workOrders.codes`).
    const item = resolveItemFromParams(params);

    // Read status from the row, not params.value: the column's valueGetter (itemStatusCellValue)
    // returns the effective-status label so AG refreshes the pill on a pending-status change, so
    // params.value is no longer the raw CodeStatus this pill styles by.
    this.$status.set(item?.status ?? null);
    this.$itemStatus.set(item?.itemStatus ?? null);
    this.$suppliedBySubcontractor.set(!!item?.suppliedBySubcontractor);
    this.$purchaseOrder.set(item?.purchaseOrder ?? null);
    this.$bill.set(item?.bill ?? null);
    this.$invoice.set(item?.invoice ?? null);

    // Set only on the SOR board mount (cellRendererParams.sorMode); All Items keeps the badges.
    this.$sorMode.set(!!params.sorMode);
    this.$hideBadges.set(!!params.hideBadges);
    this.$locked.set(params.isLocked?.(params.data) === true);
  }
}

/**
 * Column value for the status cell — the status label plus every sibling fact the cell draws. AG's
 * immutable row-diff calls refresh() only when this value changes, so a reload that flips just the
 * subcontractor flag or a finance link would otherwise leave the badges stale (WBS-11896).
 */
export function itemStatusCellValue(params: { data?: unknown; colDef?: { field?: string; cellRendererParams?: Pick<ItemStatusCellParams, 'isLocked'> } }): string {

  const item = resolveItemFromParams(params);
  if (!item) return '';

  const label = item.itemStatus?.name ?? (item.status as string | undefined) ?? '';
  // The pill draws the saved colour, so a re-stamp that keeps the name but changes the colour must still refresh.
  const color = item.itemStatus?.color ?? '';
  // A capability can flip without a status change; the term is appended only when locked so an office row's value is unchanged.
  const locked = params.colDef?.cellRendererParams?.isLocked?.(params.data) === true ? ['locked'] : [];
  // this make the agGrid trigger the refresh when the data changes.
  return [label, color, item.suppliedBySubcontractor ? 'sub' : '', item.purchaseOrder?._id ?? '', item.bill?._id ?? '', item.invoice?._id ?? '', ...locked].join('|');
}

/** Item object for sibling badge fields — parent-shaped chain path or flat EmbeddedCode row. */
function resolveItemFromParams(params: { data?: unknown; colDef?: { field?: string } } | undefined): EmbeddedCodeLike | undefined {

  const data = params?.data;
  if (!data) return undefined;

  const fieldPath = params.colDef?.field ?? '';
  if (!fieldPath.includes('.')) return data as EmbeddedCodeLike;

  // Drop the leaf (status / itemStatus) so the remainder is the embedded item path.
  const itemPath = fieldPath.split('.').slice(0, -1).join('.');
  if (!itemPath) return data as EmbeddedCodeLike;

  const item = itemPath.split('.').reduce<unknown>((acc, part) => (acc as Record<string, unknown> | null | undefined)?.[part], data);
  return (item as EmbeddedCodeLike | undefined) ?? undefined;
}
